import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Collapse, Input, Segmented, Space, Tag, Typography, message } from 'antd';
import { ApiOutlined, CopyOutlined, KeyOutlined, SearchOutlined } from '@ant-design/icons';
import { API_URL } from '../../config/runtimeConfig';
import './ApiDocumentationPage.css';

const { Title, Text, Paragraph } = Typography;
const BASE_URL = API_URL || window.location.origin;

const receiptBody = `{
  "contact_id": 15,
  "receipt_date": "2026-07-12",
  "due_date": "2026-08-11",
  "subtotal": 1000.00,
  "discount": 0,
  "vat_mode": "exclude",
  "tax_rate": 7,
  "tax_amount": 70.00,
  "total_amount": 1070.00,
  "notes": "สร้างจากระบบภายนอก",
  "items": [
    {
      "product_id": 21,
      "product_unit_id": 42,
      "description": "ค่าบริการรายเดือน",
      "quantity": 1,
      "unit_price": 1000.00,
      "total_price": 1000.00
    }
  ]
}`;

const expenseBody = `{
  "expense_date": "2026-07-12",
  "category": "ค่าใช้จ่ายสำนักงาน",
  "description": "ซื้อกระดาษ A4",
  "amount": 500.00,
  "tax_amount": 35.00,
  "subtotal": 500.00,
  "discount": 0,
  "vat_mode": "exclude",
  "tax_rate": 7,
  "payment_method": "bank_transfer",
  "supplier_id": 8,
  "status": "pending",
  "notes": "เลขที่ใบกำกับภาษี TAX-001",
  "items": [
    {
      "description": "กระดาษ A4 80 แกรม",
      "quantity": 5,
      "unit_price": 100.00
    }
  ]
}`;

const endpointGroups = [
  {
    key: 'receipts', label: 'ใบเสร็จและใบแจ้งหนี้', endpoints: [
      { method: 'GET', path: '/api/receipts', title: 'รายการใบเสร็จ/ใบแจ้งหนี้', query: 'page, limit, search, status, contact_id, start_date, end_date', permission: 'receipts.read' },
      { method: 'GET', path: '/api/receipts/:id', title: 'รายละเอียดเอกสารพร้อมรายการ', permission: 'receipts.read' },
      { method: 'POST', path: '/api/receipts', title: 'สร้างใบแจ้งหนี้', permission: 'receipts.create', body: receiptBody, note: 'ระบบสร้าง invoice_number และจองสต็อกตาม product_unit_id โดยยังไม่ตัดจริง จนกว่าจะยืนยันส่งมอบ; API เดิมที่ไม่ส่ง product_unit_id จะใช้หน่วยฐาน factor 1' },
      { method: 'PUT', path: '/api/receipts/:id', title: 'แก้ไขใบแจ้งหนี้', permission: 'receipts.update', body: receiptBody, note: 'แก้ไขรายการได้ก่อนยืนยันส่งมอบ ระบบจะ release และสร้าง Reservation ใหม่ หลังส่งมอบต้องใช้ workflow คืนสินค้า' },
      { method: 'PUT', path: '/api/receipts/:id/pay', title: 'บันทึกรับชำระเงิน', permission: 'receipts.update', body: `{
  "paid_amount": 1070.00,
  "payment_method": "bank_transfer",
  "payment_date": "2026-07-12",
  "notes": "โอนเข้าธนาคาร"
}`, note: 'หากรับครบ ระบบเปลี่ยนสถานะเป็น paid และสร้าง receipt_number; หากรับไม่ครบจะเป็น partial' },
      { method: 'DELETE', path: '/api/receipts/:id', title: 'ลบเอกสาร', permission: 'receipts.delete', note: 'ลบได้เฉพาะก่อนส่งมอบ และระบบจะ release Reservation; เอกสารที่ส่งมอบแล้วต้องสร้าง Return/Reversal' }
    ]
  },
  {
    key: 'expenses', label: 'ค่าใช้จ่าย', endpoints: [
      { method: 'GET', path: '/api/expenses', title: 'รายการค่าใช้จ่าย', query: 'page, limit, search, category, status, supplier_id, project_id, start_date, end_date', permission: 'expenses.read' },
      { method: 'GET', path: '/api/expenses/:id', title: 'รายละเอียดค่าใช้จ่ายพร้อมรายการ', permission: 'expenses.read' },
      { method: 'POST', path: '/api/expenses', title: 'สร้างค่าใช้จ่าย', permission: 'expenses.create', body: expenseBody, note: 'amount คือยอดก่อนภาษี และ backend คำนวณ total_amount = amount + tax_amount; supplier_id และ project_id ไม่บังคับ' },
      { method: 'PUT', path: '/api/expenses/:id', title: 'แก้ไขค่าใช้จ่าย', permission: 'expenses.update', body: expenseBody },
      { method: 'PUT', path: '/api/expenses/:id/approve', title: 'อนุมัติค่าใช้จ่าย', permission: 'expenses.update' },
      { method: 'DELETE', path: '/api/expenses/:id', title: 'ลบค่าใช้จ่าย', permission: 'expenses.delete' }
    ]
  },
  {
    key: 'contacts', label: 'ลูกค้าและผู้จำหน่าย', endpoints: [
      { method: 'GET', path: '/api/contacts', title: 'ค้นหารายชื่อ', query: 'page, limit, search, type, insurance_status', permission: 'contacts.read' },
      { method: 'GET', path: '/api/contacts/customers/dropdown', title: 'รายชื่อลูกค้าสำหรับ Dropdown', permission: 'contacts.read' },
      { method: 'GET', path: '/api/contacts/suppliers/dropdown', title: 'รายชื่อผู้จำหน่ายสำหรับ Dropdown', permission: 'contacts.read' },
      { method: 'GET', path: '/api/contacts/:id', title: 'รายละเอียดรายชื่อ', permission: 'contacts.read' },
      { method: 'POST', path: '/api/contacts', title: 'สร้างลูกค้า/ผู้จำหน่าย', permission: 'contacts.create', body: `{
  "name": "บริษัท ตัวอย่าง จำกัด",
  "type": "customer",
  "company_name": "บริษัท ตัวอย่าง จำกัด",
  "tax_id": "0100000000000",
  "phone": "0812345678",
  "email": "account@example.com",
  "address": "กรุงเทพมหานคร",
  "receivable_account_id": 12,
  "revenue_account_id": 41
}` },
      { method: 'PUT', path: '/api/contacts/:id', title: 'แก้ไขรายชื่อ', permission: 'contacts.update' },
      { method: 'DELETE', path: '/api/contacts/:id', title: 'ปิดการใช้งานรายชื่อ', permission: 'contacts.delete' }
    ]
  },
  {
    key: 'products', label: 'สินค้า', endpoints: [
      { method: 'GET', path: '/api/products', title: 'รายการสินค้า', query: 'page, limit, search, category, low_stock', permission: 'products.read' },
      { method: 'GET', path: '/api/products/:id', title: 'รายละเอียดสินค้า', permission: 'products.read' },
      { method: 'GET', path: '/api/products/barcode/:barcode', title: 'ค้นหาจากบาร์โค้ด', permission: 'products.read' },
      { method: 'GET', path: '/api/products/families', title: 'สินค้าแม่พร้อมจำนวน Variant', permission: 'products.read' },
      { method: 'POST', path: '/api/products/families', title: 'สร้างสินค้าแม่และนิยามคุณลักษณะ', permission: 'products.create' },
      { method: 'GET', path: '/api/products/families/:familyId/variants', title: 'Variant/SKU ของสินค้าแม่', permission: 'products.read' },
      { method: 'POST', path: '/api/products/families/:familyId/variants', title: 'สร้าง Variant พร้อมหน่วยและ Warranty', permission: 'products.create' },
      { method: 'GET', path: '/api/products/:id/units', title: 'หน่วยหลายระดับของ SKU', permission: 'products.read' },
      { method: 'PUT', path: '/api/products/:id/units', title: 'ตั้งค่าหน่วยฐาน แพ็ค ลัง บาร์โค้ดและราคา', permission: 'products.update' },
      { method: 'POST', path: '/api/products', title: 'สร้างสินค้า', permission: 'products.create', body: `{
  "name": "สินค้า A",
  "description": "รายละเอียดสินค้า",
  "price": 120.00,
  "cost": 80.00,
  "stock": 10,
  "min_stock": 2,
  "category_id": 3,
  "unit": "ชิ้น",
  "barcode": "885000000001"
}` },
      { method: 'PUT', path: '/api/products/:id', title: 'แก้ไขสินค้า', permission: 'products.update' },
      { method: 'PUT', path: '/api/products/:id/stock', title: 'ปรับจำนวนสต็อก', permission: 'products.update', body: `{
  "quantity": 25,
  "type": "set",
  "reason": "ตรวจนับสต็อก"
}` },
      { method: 'DELETE', path: '/api/products/:id', title: 'ปิดการใช้งานสินค้า', permission: 'products.delete' }
    ]
  },
  {
    key: 'inventory', label: 'คลังสินค้า Serial Warranty และ RMA', endpoints: [
      { method: 'GET', path: '/api/inventory/summary', title: 'สรุปสต็อกหน่วยฐาน ยอดจอง ล็อต และมูลค่า', permission: 'inventory.read' },
      { method: 'GET', path: '/api/inventory/products/:productId/lots', title: 'ล็อตสินค้าเรียง FIFO', permission: 'inventory.read' },
      { method: 'GET', path: '/api/inventory/products/:productId/movements', title: 'Stock Ledger แบบ immutable', permission: 'inventory.read' },
      { method: 'GET', path: '/api/inventory/products/:productId/serials', title: 'ทะเบียนและสถานะ Serial', permission: 'serials.read' },
      { method: 'POST', path: '/api/inventory/products/:productId/adjust', title: 'ปรับสต็อกพร้อมเหตุผล', permission: 'inventory.adjust_stock' },
      { method: 'POST', path: '/api/inventory/receipts/:id/reserve', title: 'สร้าง Reservation ใหม่แบบ FIFO', permission: 'inventory.reserve' },
      { method: 'GET', path: '/api/inventory/receipts/:id/fulfillment-preview', title: 'Preview ล็อต Serial และยอดขาดก่อนส่งมอบ', permission: 'inventory.fulfill' },
      { method: 'PUT', path: '/api/inventory/receipts/:id/items/:itemId/serials', title: 'เลือก Serial ที่จะส่งมอบ', permission: 'serials.reserve' },
      { method: 'POST', path: '/api/inventory/receipts/:id/confirm-fulfillment', title: 'ยืนยันส่งมอบ ตัด FIFO สร้าง Warranty และ Draft Journal', permission: 'inventory.fulfill', body: `{
  "fulfilled_date": "2026-07-14",
  "allow_negative": false,
  "reason": null
}` },
      { method: 'POST', path: '/api/inventory/serials/:id/resolve', title: 'แทน Serial TMP ด้วย Serial จริงโดยรักษาประวัติ', permission: 'serials.update' },
      { method: 'GET', path: '/api/inventory/sales-returns', title: 'รายการรับคืนจากการขาย', permission: 'sales_returns.read' },
      { method: 'POST', path: '/api/inventory/sales-returns', title: 'สร้างเอกสารรับคืน', permission: 'sales_returns.create' },
      { method: 'POST', path: '/api/inventory/sales-returns/:id/confirm', title: 'ยืนยันคืนสต็อกและสร้าง Draft Reversal', permission: 'sales_returns.approve' },
      { method: 'GET', path: '/api/warranties', title: 'ทะเบียนรับประกัน', permission: 'warranty.read' },
      { method: 'GET', path: '/api/warranties/claims/list', title: 'งาน RMA', permission: 'rma.read' },
      { method: 'POST', path: '/api/warranties/claims', title: 'เปิดงาน RMA', permission: 'rma.create' },
      { method: 'PATCH', path: '/api/warranties/claims/:id/status', title: 'อัปเดตสถานะ ซ่อม/เปลี่ยน/ปิดงาน', permission: 'rma.update' }
    ]
  },
  {
    key: 'quotes', label: 'ใบเสนอราคา', endpoints: [
      { method: 'GET', path: '/api/quotes', title: 'รายการใบเสนอราคา', permission: 'quotes.read' },
      { method: 'GET', path: '/api/quotes/:id', title: 'รายละเอียดใบเสนอราคา', permission: 'quotes.read' },
      { method: 'POST', path: '/api/quotes', title: 'สร้างใบเสนอราคา', permission: 'quotes.create', body: receiptBody.replace('"receipt_date"', '"quote_date"').replace('"due_date"', '"valid_until"') },
      { method: 'PUT', path: '/api/quotes/:id', title: 'แก้ไขใบเสนอราคา', permission: 'quotes.update' },
      { method: 'POST', path: '/api/quotes/:id/convert-to-receipt', title: 'แปลงเป็นใบแจ้งหนี้', permission: 'receipts.create' },
      { method: 'DELETE', path: '/api/quotes/:id', title: 'ลบใบเสนอราคา', permission: 'quotes.delete' }
    ]
  },
  {
    key: 'storage', label: 'พื้นที่จัดเก็บเอกสาร', endpoints: [
      { method: 'GET', path: '/api/storage/settings', title: 'สถานะ Storage Provider', permission: 'Super Admin' },
      { method: 'PUT', path: '/api/storage/settings/draft', title: 'บันทึกการตั้งค่า Wizard', permission: 'Super Admin', body: `{ "provider": "google_drive", "client_id": "...", "client_secret": "..." }` },
      { method: 'POST', path: '/api/storage/oauth/google/start', title: 'เริ่ม Google OAuth', permission: 'Super Admin' },
      { method: 'GET', path: '/api/storage/folders', title: 'รายการโฟลเดอร์ Google Drive', permission: 'Super Admin' },
      { method: 'POST', path: '/api/storage/test', title: 'ทดสอบสร้าง อ่าน และลบไฟล์', permission: 'Super Admin' },
      { method: 'POST', path: '/api/storage/activate', title: 'เปิดใช้ Provider', permission: 'Super Admin', body: `{ "provider": "google_drive" }` },
      { method: 'GET', path: '/api/files/:id/content', title: 'Preview/Download ไฟล์ private', permission: 'Authenticated user' },
      { method: 'DELETE', path: '/api/files/:id', title: 'ลบไฟล์จาก Provider เดิม', permission: 'เจ้าของไฟล์หรือ Super Admin' }
    ]
  },
  {
    key: 'accounting', label: 'บัญชีและรายงาน', endpoints: [
      { method: 'GET', path: '/api/accounting/accounts', title: 'ผังบัญชี', permission: 'สิทธิ์ผู้ใช้เจ้าของ Token' },
      { method: 'GET', path: '/api/accounting/reports/trial-balance', title: 'งบทดลอง', query: 'start_date, end_date' },
      { method: 'GET', path: '/api/accounting/reports/income-statement', title: 'งบกำไรขาดทุน', query: 'start_date, end_date' },
      { method: 'GET', path: '/api/accounting/reports/financial-position', title: 'งบแสดงฐานะทางการเงิน', query: 'end_date' },
      { method: 'GET', path: '/api/accounting/reports/general-ledger', title: 'บัญชีแยกประเภท', query: 'account_id, start_date, end_date' },
      { method: 'POST', path: '/api/accounting/receipts/:id/post', title: 'ลงบัญชีใบเสร็จ', body: '{}' },
      { method: 'POST', path: '/api/accounting/expenses/:id/post', title: 'ลงบัญชีค่าใช้จ่าย', body: '{}' }
    ]
  },
  {
    key: 'ai-expense-import', label: 'AI, Expense Import และ LINE OA', endpoints: [
      { method: 'GET', path: '/api/ai-settings/providers', title: 'รายการ LLM Provider (ไม่คืน API key)', permission: 'ai_settings.manage' },
      { method: 'PUT', path: '/api/ai-settings/providers/:provider', title: 'ตั้งค่า Gemini, OpenAI หรือ DeepSeek', permission: 'ai_settings.manage', body: '{ "model_id": "MODEL_ID", "api_key": "SECRET", "is_enabled": true, "priority": 10 }' },
      { method: 'POST', path: '/api/ai-settings/providers/:provider/test', title: 'ทดสอบ LLM Provider', permission: 'ai_settings.manage', body: '{}' },
      { method: 'POST', path: '/api/expense-imports/text', title: 'แยกรายรับและรายจ่ายจากข้อความหลายบรรทัด', permission: 'expense_import.create', body: '{ "text": "ค่าข้าว 100\\nขายโปรแกรมได้ 1000\\nรายได้ค่าบริการ 500\\nเติมน้ำมัน 500" }', note: 'แต่ละธุรกรรมจะเป็นงาน IMP แยกกัน หากไม่ระบุวันที่จะใช้วันที่รับข้อความตามเวลาไทย' },
      { method: 'POST', path: '/api/expense-imports/file', title: 'นำเข้า JPG, PNG หรือ PDF (multipart field: file)', permission: 'expense_import.create' },
      { method: 'GET', path: '/api/expense-imports', title: 'รายการงานนำเข้าที่รอตรวจ', permission: 'expense_import.read', query: 'status, page, limit' },
      { method: 'PUT', path: '/api/expense-imports/:id', title: 'แก้ข้อมูลที่ AI อ่านได้', permission: 'expense_import.update' },
      { method: 'POST', path: '/api/expense-imports/:id/confirm', title: 'ยืนยันสร้าง EXP', permission: 'expense_import.confirm', body: '{}' },
      { method: 'POST', path: '/api/expense-imports/:id/retry', title: 'ประมวลผลใหม่', permission: 'expense_import.retry', body: '{}' },
      { method: 'GET', path: '/api/integrations/line', title: 'สถานะและ Webhook URL ของ LINE OA', permission: 'line_integration.manage' },
      { method: 'POST', path: '/api/integrations/line/webhook-test', title: 'จำลองส่งข้อความหรือไฟล์ผ่าน LINE โดยไม่เรียก LINE API', permission: 'line_integration.manage', note: 'ใช้ multipart/form-data โดยส่ง field text หรือ file (JPG, PNG, PDF ไม่เกิน 10 MB)' },
      { method: 'POST', path: '/api/integrations/line/webhook', title: 'LINE public webhook (ตรวจ x-line-signature)', permission: 'Public signed webhook' }
    ]
  }
];

const methodColor = { GET: 'blue', POST: 'green', PUT: 'orange', PATCH: 'gold', DELETE: 'red' };

const CodeBlock = ({ children }) => {
  const copy = () => navigator.clipboard.writeText(children).then(() => message.success('คัดลอกแล้ว'));
  return <div className="api-code"><Button size="small" icon={<CopyOutlined />} onClick={copy}>คัดลอก</Button><pre>{children}</pre></div>;
};

const Endpoint = ({ endpoint, authHeader }) => {
  const url = `${BASE_URL}${endpoint.path}`;
  const curl = `curl -X ${endpoint.method} "${url}" \\
  -H "${authHeader}" \\
  -H "Content-Type: application/json"${endpoint.body ? ` \\
  -d '${endpoint.body.replace(/\n/g, '').replace(/\s{2,}/g, ' ')}'` : ''}`;
  return {
    key: `${endpoint.method}-${endpoint.path}`,
    label: <div className="endpoint-label"><Tag color={methodColor[endpoint.method]}>{endpoint.method}</Tag><Text code>{endpoint.path}</Text><span>{endpoint.title}</span></div>,
    children: <div className="endpoint-detail">
      <Space wrap><Text strong>สิทธิ์:</Text><Tag>{endpoint.permission || 'Authenticated'}</Tag></Space>
      {endpoint.query && <Paragraph><Text strong>Query parameters:</Text> <Text code>{endpoint.query}</Text></Paragraph>}
      {endpoint.note && <Alert type="warning" showIcon message={endpoint.note} />}
      {endpoint.body && <><Text strong>Request body</Text><CodeBlock>{endpoint.body}</CodeBlock></>}
      <Text strong>ตัวอย่าง cURL</Text><CodeBlock>{curl}</CodeBlock>
    </div>
  };
};

const ApiDocumentationPage = () => {
  const [search, setSearch] = useState('');
  const [authMode, setAuthMode] = useState('API Token');
  const authHeader = authMode === 'API Token' ? 'X-API-Token: YOUR_API_TOKEN' : 'Authorization: Bearer YOUR_ACCESS_TOKEN';
  const groups = useMemo(() => endpointGroups.map(group => ({
    ...group,
    endpoints: group.endpoints.filter(item => `${item.method} ${item.path} ${item.title}`.toLowerCase().includes(search.toLowerCase()))
  })).filter(group => group.endpoints.length), [search]);

  return <div className="api-docs-page">
    <div className="api-docs-hero">
      <div><Title level={2}><ApiOutlined /> API Documentation</Title><Paragraph>คู่มืออ้างอิงจาก API ของระบบเวอร์ชันปัจจุบัน · อัปเดต 14 กรกฎาคม 2026</Paragraph></div>
      <Button href="/settings/api" icon={<KeyOutlined />}>จัดการ API Token</Button>
    </div>
    <Alert showIcon type="info" message="Base URL" description={<Text code>{BASE_URL}</Text>} />
    <Card className="getting-started-card" title="เริ่มต้นใช้งาน">
      <Paragraph>สร้าง Token ที่หน้า <Text code>/settings/api</Text> และเก็บค่า Token ทันที เพราะระบบจะแสดงเพียงครั้งเดียว API Token จะทำงานภายใต้สิทธิ์ของผู้ใช้เจ้าของ Token และสามารถยกเลิกได้จากหน้าจัดการ</Paragraph>
      <Segmented value={authMode} onChange={setAuthMode} options={['API Token', 'User JWT']} />
      <CodeBlock>{authMode === 'API Token' ? `X-API-Token: YOUR_API_TOKEN` : `Authorization: Bearer YOUR_ACCESS_TOKEN`}</CodeBlock>
      <Alert type="warning" showIcon message="ห้ามฝัง API Token ใน Frontend หรือ Mobile App ที่ผู้ใช้สามารถเปิดดู source ได้ ควรเรียกผ่าน backend ของระบบภายนอกและเก็บ Token ใน secret/environment variable" />
    </Card>
    <Input size="large" prefix={<SearchOutlined />} placeholder="ค้นหา endpoint เช่น receipt, expense, /api/products" value={search} onChange={event => setSearch(event.target.value)} allowClear />
    <div className="api-docs-layout">
      <aside>{groups.map(group => <a key={group.key} href={`#${group.key}`}>{group.label}<Tag>{group.endpoints.length}</Tag></a>)}</aside>
      <main>
        {groups.map(group => <section id={group.key} key={group.key}>
          <Title level={3}>{group.label}</Title>
          <Collapse items={group.endpoints.map(endpoint => Endpoint({ endpoint, authHeader }))} />
        </section>)}
        {!groups.length && <Card><Text>ไม่พบ endpoint ที่ค้นหา</Text></Card>}
        <section id="responses"><Title level={3}>รูปแบบ Response และ Error</Title>
          <CodeBlock>{`// สำเร็จ\n{ "success": true, "data": { ... } }\n\n// Validation ไม่ผ่าน (400)\n{ "success": false, "message": "Validation failed", "errors": [...] }\n\n// Token ไม่ถูกต้อง (401)\n{ "success": false, "message": "Invalid token", "code": "INVALID_TOKEN" }\n\n// ไม่มีสิทธิ์ (403)\n{ "success": false, "message": "Access denied - insufficient permissions" }`}</CodeBlock>
          <Paragraph>รหัสที่ควรรองรับ: <Tag>200 OK</Tag><Tag>201 Created</Tag><Tag>400 Validation</Tag><Tag>401 Authentication</Tag><Tag>403 Permission</Tag><Tag>404 Not found</Tag><Tag>500 Server error</Tag></Paragraph>
        </section>
      </main>
    </div>
  </div>;
};

export default ApiDocumentationPage;
